import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Globe, Award, ShieldCheck } from 'lucide-react'

const partnerCategories = [
  {
    title: 'Technology Partners',
    partners: [
      { name: 'Microsoft', description: 'Azure Cloud & Enterprise Software' },
      { name: 'AWS', description: 'Cloud Infrastructure Services' },
      { name: 'Google Cloud', description: 'Cloud Platform & AI/ML Services' },
      { name: 'Oracle', description: 'Database & Enterprise Solutions' },
    ],
  },
  {
    title: 'OEM Partners',
    partners: [
      { name: 'Cisco', description: 'Networking & Security Infrastructure' },
      { name: 'Dell Technologies', description: 'Servers & Storage Solutions' },
      { name: 'HP Enterprise', description: 'Computing & Printing Solutions' },
      { name: 'Lenovo', description: 'Hardware & Data Center Solutions' },
    ],
  },
  {
    title: 'Security Partners',
    partners: [
      { name: 'Palo Alto Networks', description: 'Cybersecurity Solutions' },
      { name: 'Fortinet', description: 'Network Security' },
      { name: 'Hikvision', description: 'Surveillance Systems' },
      { name: 'Bosch', description: 'Security & Communication' },
    ],
  },
  {
    title: 'Education Partners',
    partners: [
      { name: 'Smart Technologies', description: 'Interactive Displays' },
      { name: 'BenQ', description: 'Smart Classroom Solutions' },
      { name: 'Promethean', description: 'Interactive Flat Panels' },
      { name: 'Newline', description: 'Collaboration Displays' },
    ],
  },
]

const Partners = () => {
  return (
    <>
      <Helmet>
        <title>Our Partners | Eonixa Dynamics</title>
        <meta name="description" content="Strategic partnerships with Microsoft, AWS, Google Cloud, Cisco, Dell and leading technology providers" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Our Partners</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                Strategic alliances with world-class technology providers and OEMs.
                These partnerships enable us to deliver best-in-class solutions.
              </p>
            </div>
          </FadeIn>

          {/* Partnership Stats */}
          <FadeIn delay={0.2}>
            <div className="grid gap-8 sm:grid-cols-3 mb-16">
              <div className="rounded-2xl bg-white p-8 text-center shadow-soft">
                <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">50+</p>
                <p className="mt-2 text-gray-600">Partner Organizations</p>
              </div>
              <div className="rounded-2xl bg-white p-8 text-center shadow-soft">
                <Globe className="mx-auto mb-4 h-10 w-10 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">20+</p>
                <p className="mt-2 text-gray-600">Global OEMs</p>
              </div>
              <div className="rounded-2xl bg-white p-8 text-center shadow-soft">
                <Award className="mx-auto mb-4 h-10 w-10 text-primary-600" />
                <p className="text-4xl font-bold text-gray-900">100+</p>
                <p className="mt-2 text-gray-600">Certifications</p>
              </div>
            </div>
          </FadeIn>

          {/* Partner Categories */}
          <div className="grid gap-12">
            {partnerCategories.map((category, index) => (
              <FadeIn key={category.title} delay={index * 0.1}>
                <div>
                  <h2 className="mb-6 text-2xl font-bold text-gray-900">
                    {category.title}
                  </h2>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {category.partners.map((partner) => (
                      <div
                        key={partner.name}
                        className="group rounded-xl bg-white p-6 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card"
                      >
                        <div className="mb-4 flex h-20 items-center justify-center">
                          <span className="text-2xl font-bold text-gray-700 group-hover:text-primary-600 transition-colors">
                            {partner.name}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{partner.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={0.6} className="mt-16 text-center">
            <div className="rounded-2xl bg-primary-900 p-8 text-white">
              <h2 className="mb-4 text-2xl font-bold">Become Our Partner</h2>
              <p className="mx-auto max-w-2xl text-gray-200 mb-6">
                We're always open to exploring new partnerships with innovative
                technology providers and solution partners.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-base font-semibold text-primary-900 transition-all hover:bg-gray-100"
              >
                Partner With Us
              </a>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default Partners
